import React from 'react';
import {View, StyleSheet, ViewStyle, Pressable} from 'react-native';
import {spacing, corner_radius} from 'theme/Units';
import colors from 'theme/Colors';
import {UserStoryTag, Text, Icon} from 'ui-kits';
import {Priority} from '../Priority';
import {
  deleteUserStoryRequest,
  UserStory,
  setCurrentSelectedStory,
} from 'redux/slices/userStorySlice';
import {Routes} from 'navigation/Routes';
import {selectedProjectID} from 'redux/selectors/projectSelector';
import {useDispatch, useSelector} from 'react-redux';
import {UserStoryStatus} from 'constants/enums';
import {useAuthAccess, useConfimationModal} from 'hooks';

type UserStoryCardProps = {
  data: UserStory;
  style?: ViewStyle;
  navigate?: (route: string, params: any) => void;
};

export const UserStoryCard = ({
  data,
  style: overrideStyle,
  navigate,
}: UserStoryCardProps) => {
  const projectID = useSelector(selectedProjectID);
  const dispatch = useDispatch();
  const {hasWriteAccess} = useAuthAccess();
  const {showModal} = useConfimationModal();

  function goToStoryDetails() {
    dispatch(setCurrentSelectedStory(data.id));

    navigate?.(Routes.VIEW_USER_STORY, {
      id: projectID?.toString(),
      usid: data.id.toString(),
    });
  }

  function handleDeleteUserStory() {
    dispatch(deleteUserStoryRequest({id: data.id}));
  }

  function openModal() {
    showModal({
      header: 'Delete Story',
      message: 'Do you really want to delete this story?',
      onConfirmHandler: handleDeleteUserStory,
    });
  }

  return (
    <View style={[styles.card, overrideStyle]}>
      {/* Header: US ID and Status */}
      <View style={styles.header}>
        <Pressable onPress={goToStoryDetails}>
          <View style={styles.validation}>
            <Text style={styles.usId}>{data.us_id}</Text>
            {data.is_validated ? <Icon type="validate_tick" /> : null}
          </View>
        </Pressable>
        {/* when i get clear idea about the status of backlogs then i will change it */}
        <View style={styles.actionButtonWrapper}>
          <UserStoryTag
            variant={UserStoryStatus[data?.status?.id || '1']}
            text={data.status?.name || UserStoryStatus[1]}
          />
          {hasWriteAccess ? (
            <Pressable style={styles.deleteIconButton} onPress={openModal}>
              <Icon type="trash_white" />
            </Pressable>
          ) : null}
        </View>
      </View>

      {/* Title */}
      <Pressable onPress={goToStoryDetails}>
        <Text style={styles.title}>{data.title}</Text>
      </Pressable>

      {/* Responsible People */}
      <View style={styles.info}>
        <View style={styles.responsibleContainer}>
          <Icon type="responsible" size={16} />
          <Text style={styles.responsible}>
            Responsible: {data.responsible?.name}
          </Text>
        </View>

        {/* Last Updated & Rating */}
        <View style={styles.footer}>
          <View style={styles.lastUpdated}>
            <Text style={styles.updatedAt}>
              Last Updated: {new Date(data.updated_at).toLocaleDateString()}
            </Text>
            <Icon type="clock" size={16} />
          </View>
          <View style={styles.rating}>
            <Priority priority={data.priority} />
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  activeStar: {
    width: 14,
    height: 14,
    backgroundColor: colors.palette.yelloWarning,
  },
  actionButtonWrapper: {
    flexDirection: 'row',
    gap: spacing['2'],
    alignItems: 'center',
  },
  disabledStar: {
    width: 14,
    height: 14,
    backgroundColor: colors.palette.gray,
  },

  deleteIconButton: {
    backgroundColor: colors.palette.errorRedDark,
    padding: spacing['2'],
    borderRadius: corner_radius['rounded-md'],
  },

  info: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  card: {
    padding: spacing['3'],
    borderRadius: corner_radius['rounded-lg'],
    borderWidth: 1,
    borderColor: colors.palette.stroke,
    backgroundColor: colors.palette.white,
    shadowColor: colors.palette.black,
    shadowOpacity: 0.1,
    shadowRadius: 4,
    shadowOffset: {width: 0, height: 2},
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  usId: {
    fontWeight: 'bold',
    color: colors.palette.black,
  },
  title: {
    fontSize: 14,
    color: colors.palette.black,
    marginVertical: spacing[1],
  },
  responsibleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: spacing[1],
    gap: spacing['2'],
  },
  responsible: {
    fontSize: 12,
    color: colors.palette.black,
    maxWidth: 180,
  },
  icon: {
    width: 16,
    height: 16,
    flexShrink: 0,
    marginRight: spacing[1],
  },
  lastUpdated: {
    flexDirection: 'row',
    gap: spacing['1'],
  },
  footer: {
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    gap: spacing['1'],
  },
  updatedAt: {
    fontSize: 12,
    color: colors.palette.black,
  },
  rating: {
    flexDirection: 'row',
    gap: spacing['1'],
  },
  validation: {
    flexDirection: 'row',
    gap: spacing['3'],
  },
});
